// crewbit-admin/src/lib/invoiceActions.ts
//
// Pure status → InvoiceAction[] matrix for §InvoiceDetail.7.3.
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §InvoiceDetail.7.3.
//
// Returned actions inform the UI's button row + overflow. Independent of
// any UI library — caller wires onClick / disabled-tooltip per ActionId.
// Local InvoiceActionsInput keeps this helper independent of the Invoice
// type (which doesn't yet declare is_disputed / dispute_status fields,
// even though those columns exist live on `invoices`).

export type InvoiceStatus =
  | 'draft' | 'sent' | 'viewed' | 'paid' | 'overdue'
  | 'cancelled' | 'refunded' | 'partially_refunded' | 'written_off';

export type ActionId =
  | 'edit' | 'send' | 'delete' | 'send_reminder' | 'record_payment' | 'cancel'
  | 'mark_bad_debt' | 'view_receipt' | 'send_receipt' | 'refund' | 'refund_remaining'
  | 'copy_link' | 'share' | 'view_dispute' | 'duplicate_as_draft' | 'save_as_shape';

export type ActionTone = 'primary' | 'primary_amber' | 'primary_red' | 'secondary' | 'overflow';

export interface InvoiceAction {
  id: ActionId;
  label: string;
  tone: ActionTone;
  disabled?: { reason: string };
}

export interface InvoiceActionsInput {
  status: InvoiceStatus;
  is_disputed?: boolean | null;
  dispute_status?: string | null;
}

export interface InvoiceActionsArgs {
  invoice: InvoiceActionsInput;
  hasStripePaymentIntent: boolean;
  lastRemindedRecent: boolean;          // < 24h since last_reminder_sent_at
}

export function invoiceActions(args: InvoiceActionsArgs): InvoiceAction[] {
  const { invoice, hasStripePaymentIntent, lastRemindedRecent } = args;
  const { status, is_disputed, dispute_status } = invoice;
  const disputeOpen =
    !!is_disputed && dispute_status !== 'won' && dispute_status !== 'lost';
  const actions: InvoiceAction[] = [];

  switch (status) {
    case 'draft':
      actions.push({ id: 'edit', label: 'Edit', tone: 'primary' });
      actions.push({ id: 'send', label: 'Send', tone: 'primary' });
      actions.push({ id: 'copy_link', label: 'Copy link', tone: 'secondary' });
      actions.push({ id: 'delete', label: 'Delete draft', tone: 'overflow' });
      break;

    case 'sent':
    case 'viewed':
      actions.push({
        id: 'send_reminder',
        label: 'Send reminder',
        tone: lastRemindedRecent ? 'overflow' : 'secondary',
      });
      actions.push({ id: 'record_payment', label: 'Record payment', tone: 'primary' });
      actions.push({ id: 'copy_link', label: 'Copy link', tone: 'secondary' });
      actions.push({ id: 'share', label: 'Share', tone: 'secondary' });
      actions.push({
        id: 'cancel',
        label: 'Cancel invoice',
        tone: 'overflow',
        disabled: disputeOpen ? { reason: 'Resolve the dispute first.' } : undefined,
      });
      break;

    case 'overdue':
      actions.push({
        id: 'send_reminder',
        label: 'Send reminder',
        tone: lastRemindedRecent ? 'secondary' : 'primary_amber',
      });
      actions.push({ id: 'record_payment', label: 'Record payment', tone: 'primary' });
      actions.push({ id: 'copy_link', label: 'Copy link', tone: 'secondary' });
      actions.push({ id: 'share', label: 'Share', tone: 'secondary' });
      actions.push({
        id: 'cancel',
        label: 'Cancel invoice',
        tone: 'overflow',
        disabled: disputeOpen ? { reason: 'Resolve the dispute first.' } : undefined,
      });
      actions.push({ id: 'mark_bad_debt', label: 'Write off as bad debt', tone: 'overflow' });
      break;

    case 'paid':
      actions.push({ id: 'view_receipt', label: 'View receipt', tone: 'primary' });
      actions.push({ id: 'send_receipt', label: 'Send receipt to client', tone: 'secondary' });
      actions.push({
        id: 'refund',
        label: hasStripePaymentIntent ? 'Refund' : 'Mark as refunded',
        tone: 'secondary',
      });
      actions.push({ id: 'copy_link', label: 'Copy link', tone: 'secondary' });
      break;

    case 'partially_refunded':
      actions.push({ id: 'refund_remaining', label: 'Refund remaining', tone: 'primary' });
      actions.push({ id: 'view_receipt', label: 'View receipt', tone: 'secondary' });
      actions.push({ id: 'copy_link', label: 'Copy link', tone: 'secondary' });
      break;

    case 'refunded':
      actions.push({ id: 'view_receipt', label: 'View receipt', tone: 'secondary' });
      actions.push({ id: 'copy_link', label: 'Copy link', tone: 'overflow' });
      break;

    case 'cancelled':
      actions.push({ id: 'view_receipt', label: 'View invoice', tone: 'secondary' });
      actions.push({ id: 'duplicate_as_draft', label: 'Duplicate as draft', tone: 'overflow' });
      break;

    case 'written_off':
      actions.push({ id: 'view_receipt', label: 'View invoice', tone: 'secondary' });
      break;
  }

  // Phase 5 Migration B sub-track (c) — "Save as starting point" overflow
  // action on any post-draft invoice with a real shape. Drafts excluded
  // (their shape isn't real yet). Refunded / cancelled / written-off
  // included — operator might still want to template their shape.
  if (status !== 'draft') {
    actions.push({ id: 'save_as_shape', label: 'Save as a starting point', tone: 'overflow' });
  }

  if (disputeOpen) {
    actions.unshift({ id: 'view_dispute', label: 'View dispute details', tone: 'primary_red' });
  }

  return actions;
}
